import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { getUsers, deleteUser } from '../../../models/userModel';
import Swal from 'sweetalert2'; 
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

// Debounced Input for Global Filter
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (value !== initialValue) {
        onChange(value);
      }
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, initialValue, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}


function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  


  const initialPagination = () => {
    const storedPagination = localStorage.getItem('usersTablePagination');
    if (storedPagination) {
      return JSON.parse(storedPagination);
    }
    return {
      pageIndex: 0,
      pageSize: 10,
    };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    localStorage.setItem('usersTablePagination', JSON.stringify(pagination));
  }, [pagination]);

  const columns = useMemo(() => [
    {
      id: 'user_full_name',
      accessorFn: row => row.user_full_name,
      header: 'Name',
      cell: ({ row }) => {
        const user = row.original;
        const missingDept = (user.department == null || user.department === '' || Number(user.department) <= 0 || 
                            user.department_id == null || user.department_id === '' || Number(user.department_id) <= 0);
 
        return (
          <div>
            <span className="font-medium text-gray-800">{user.user_full_name}</span>
            <div className="flex items-center space-x-1 mt-1">
              {user.it_support == 1 && (
                <span title="IT Support" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  IT
                </span>
              )}
              {user.supervisor == 1 && (
                <span title="Supervisor" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                  SUP
                </span>
              )}
              {user.manager == 1 && (
                <span title="Manager" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  MGR
                </span>
              )}
              {user.hod == 1 && (
                <span title="HOD" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  HOD
                </span>
              )}
              {user.director == 1 && (
                <span title="Director" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  DIR
                </span>
              )}
              {!!(user.user_delegate && user.user_delegate != 0) && (
                <span title="Has Delegate" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                  DEL
                </span>
              )} 
            </div>
          </div>
        );
      },
    },
    {
      id: 'user_email',
      accessorFn: row => row.user_email,
      header: 'Email / Last Activity',
      cell: ({ row }) => {
        const user = row.original;
        return (
            <div>
                <span>{user.user_email}</span>
                <div className="text-xs text-gray-500 whitespace-nowrap">
                    {user.user_time ? new Date(user.user_time * 1000).toLocaleString() : 'No activity'}
                </div>
            </div>
        )
      },
      enableSorting: false,
    },
    {
      id: 'department_name',
      header: 'Division',
      cell: ({ row }) => {
        const user = row.original;
        const missingDept = (user.department == null || user.department === '' || Number(user.department) <= 0 ||
                              user.department_id == null || user.department_id === '' || Number(user.department_id) <= 0);
        return (
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{user.department_name || '—'}</span>
              {missingDept && (
                <span title="Missing Department Info - Needs Update" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  !DEPT
                </span>
              )}
            </div>
            {user.parent_department_name && (
              <div className="text-xs text-gray-500">{user.parent_department_name}</div>
            )}
          </div>
        );
      },
    },
    {
        accessorKey: 'role_name',
        header: 'Role',
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <div className="flex items-center space-x-1">
          <Link
            to={`edit/${row.original.user_id}`}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
          >
            Edit
          </Link>
          {can('Delete Users') && (
            <button
              type="button"
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
              onClick={() => handleDelete(row.original.user_id, row.original.user_full_name)}
            >
              Delete
            </button>
          )}
        </div>
      ),
      enableSorting: false,
    },
  ], [can]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      if (globalFilter) {
        params.append('filter', globalFilter);
      }
      if (sorting.length > 0) {
        params.append('sortBy', sorting[0].id);
        params.append('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      }
       
      const response = await getUsers(params);
      if (response.status === 1 && response.data && response.data.rows && response.data.meta) {
        setUsers(response.data.rows);
        setRowCount(response.data.meta.totalItems);
      } else {
        toast.error(response.message || 'Failed to load users');
        setUsers([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.message || 'An error occurred while loading users');
      setUsers([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter, sorting]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);


  const table = useReactTable({
    data: users,
    columns,
    state: {
      pagination,
      globalFilter,
      sorting,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    debugTable: false, 
  });

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the user "${name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-accent-danger)',
      cancelButtonColor: 'var(--color-neutral-500)',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteUser(id);
          if (response.status === 1) {
            toast.success(`User "${name}" deleted successfully!`);
            fetchUsers();
          } else {
            toast.error(response.message || 'Failed to delete user');
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          toast.error(error.response?.data?.message || 'An error occurred while deleting the user');
        }
      }
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
            <div className="flex justify-center items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading users...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Users Management</h1>
                <p className="text-xs text-gray-500 mb-1">Manage system users, roles and permissions</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount} Total Users
                  </span>
                  {pagination.pageIndex > 0 && (
                    <span className="text-xs text-gray-400 ml-2">
                      • Page {pagination.pageIndex + 1}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={() => fetchUsers()}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                {/* Search Input */}
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={value => {
                    setGlobalFilter(String(value));
                    setPagination(p => ({ ...p, pageIndex: 0 }));
                  }}
                  className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search users..."
                />
                
                {/* Create Button */}
                {can('Create Users') && (
                  <Link
                    to="create"
                    className="inline-flex items-center px-3 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                    style={{ 
                      backgroundColor: '#0087D4', 
                      boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                    }}
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create New User
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      scope="col"
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                      }`}
                      style={{ color: '#0087D4' }}
                      onClick={header.column.getToggleSortingHandler()}
                      title={
                        header.column.getCanSort() 
                          ? header.column.getNextSortingOrder() === 'asc' 
                            ? 'Sort ascending' 
                            : header.column.getNextSortingOrder() === 'desc' 
                            ? 'Sort descending' 
                            : 'Clear sort' 
                          : undefined
                      }
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <span className="ml-1 text-blue-600">↑</span>,
                        desc: <span className="ml-1 text-blue-600">↓</span>,
                      }[header.column.getIsSorted()] ?? null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && users.length > 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-6 text-center">
                    <p className="text-gray-600">
                      {globalFilter ? "No users found matching your search." : "No users found"}
                    </p>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-blue-50">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-3 py-2 text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rowCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              {/* Results info */}
              <div className="text-gray-500">
                Showing {(pagination.pageIndex * pagination.pageSize) + 1} to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount)} of {rowCount} results
              </div>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 40, 50].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              {/* Navigation buttons */}
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Current page indicator */}
              <div className="flex items-center space-x-1">
                <span className={`px-3 py-1.5 border rounded text-xs font-medium bg-blue-600 text-white border-blue-600`}>
                  {pagination.pageIndex + 1}
                </span>
              </div>
              
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default UsersList;
